/**
 * Dechign before/after slider — vanilla, defer-loaded.
 * Pairs with [data-before-after] containers in sections.
 *
 * Behavior:
 * - Pointerdrag on container updates --slider-position
 * - Handle button supports keyboard (Arrow keys, Home, End, PageUp/Down)
 * - Respects prefers-reduced-motion for the initial centering animation
 */

(() => {
  if (window.__dechignBaInit) return;
  window.__dechignBaInit = true;

  const STEP = 5;        // 5% per arrow press
  const BIG_STEP = 10;   // PageUp/PageDown

  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

  const init = (root) => {
    const handle = root.querySelector('[data-ba-handle]');
    if (!handle) return;

    let dragging = false;

    const setPosition = (pct) => {
      const clamped = clamp(Math.round(pct), 0, 100);
      root.style.setProperty('--slider-position', clamped + '%');
      handle.setAttribute('aria-valuenow', String(clamped));
    };

    const updateFromClientX = (clientX) => {
      const rect = root.getBoundingClientRect();
      if (rect.width <= 0) return;
      const x = clamp(clientX - rect.left, 0, rect.width);
      setPosition((x / rect.width) * 100);
    };

    const onPointerDown = (event) => {
      dragging = true;
      try { root.setPointerCapture(event.pointerId); } catch (e) { /* noop */ }
      updateFromClientX(event.clientX);
      event.preventDefault();
    };
    const onPointerMove = (event) => {
      if (!dragging) return;
      updateFromClientX(event.clientX);
    };
    const onPointerUp = (event) => {
      if (!dragging) return;
      dragging = false;
      try { root.releasePointerCapture(event.pointerId); } catch (e) { /* noop */ }
    };

    root.addEventListener('pointerdown', onPointerDown);
    root.addEventListener('pointermove', onPointerMove);
    root.addEventListener('pointerup', onPointerUp);
    root.addEventListener('pointercancel', onPointerUp);

    handle.addEventListener('keydown', (event) => {
      const current = parseInt(handle.getAttribute('aria-valuenow'), 10) || 50;
      let next = current;
      switch (event.key) {
        case 'ArrowLeft':
        case 'ArrowDown':
          next = current - STEP;
          break;
        case 'ArrowRight':
        case 'ArrowUp':
          next = current + STEP;
          break;
        case 'PageDown':
          next = current - BIG_STEP;
          break;
        case 'PageUp':
          next = current + BIG_STEP;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = 100;
          break;
        default:
          return;
      }
      event.preventDefault();
      setPosition(next);
    });

    /* Prevent native image drag on the overlay */
    root.querySelectorAll('img').forEach((img) => {
      img.addEventListener('dragstart', (e) => e.preventDefault());
    });

    /* Ensure initial value is in sync */
    const initial = parseInt(handle.getAttribute('aria-valuenow'), 10) || 50;
    setPosition(initial);
  };

  const attach = () => {
    document.querySelectorAll('[data-before-after]').forEach((el) => {
      if (el.dataset.baReady === '1') return;
      el.dataset.baReady = '1';
      init(el);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attach);
  } else {
    attach();
  }

  document.addEventListener('shopify:section:load', attach);
})();
